import React, { useState } from "react";
import { config } from "../../../../config";

const UpdateLive = ({ 
  formData, 
  handleChange, 
  handleBonusLinkChange, 
  addBonusLink, 
  removeBonusLink,
  handleSubmit, 
  setVideo, 
  uploadProgress, 
  loading, 
  onCancel, 
  error 
}) => {
  const [uploadVideo, setUploadVideo] = useState(formData.video && !formData.video.startsWith('http'));

  return (
    <div className="p-6 bg-white rounded-lg">
      <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-2">Update Live Page</h2>

      {/* Status Indicators */}
      {loading && (
        <div className="mb-4 p-3 bg-blue-100 border-l-4 border-blue-500 text-blue-700">
          <p>Updating live page...</p>
        </div>
      )}
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border-l-4 border-red-500 text-red-700">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Slug */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Page Slug</label>
          <input
            type="text"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
            placeholder="e.g., summer-sale"
          />
          <p className="mt-1 text-sm text-gray-500">Unique identifier for this live page</p>
        </div>

        {/* Video Source Toggle */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={uploadVideo}
              onChange={(e) => {
                setUploadVideo(e.target.checked);
                if (e.target.checked) {
                  setVideo(null);
                }
              }}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <span className="text-gray-700 font-medium">Upload video file instead of using a link</span>
          </label>
        </div>

        {/* Video Upload or URL */}
        {uploadVideo ? (
          <div className="space-y-1">
            {uploadProgress > 0 && (
              <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
                <div
                  className="bg-blue-600 h-4 rounded-full flex items-center justify-center"
                  style={{ width: `${uploadProgress}%` }}
                >
                  <span className="text-white text-xs font-bold">{uploadProgress}%</span>
                </div>
              </div>
            )}
            <label className="block text-sm font-medium text-gray-700">Video File</label>
            <input
              type="file"
              name="video"
              onChange={(e) => setVideo(e.target.files[0])}
              accept="video/*"
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
            />
            <p className="mt-1 text-sm text-gray-500">MP4, MOV, or AVI. Max 50MB.</p>
          </div>
        ) : (
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Video URL</label>
            <input
              type="text"
              name="video_url"
              value={formData.video_url}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              placeholder="https://example.com/video.mp4"
            />
            <p className="mt-1 text-sm text-gray-500">Direct link to your video file</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Viewer Count */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Viewer Count</label>
            <input
              type="number"
              name="seenPeople"
              value={formData.seenPeople}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              placeholder="e.g. 1250"
            />
            <p className="mt-1 text-sm text-gray-500">Number of people watching</p>
          </div>

          {/* Offer Time */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Offer Timer (seconds)</label>
            <input
              type="number"
              name="offerTime"
              value={formData.offerTime}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              placeholder="e.g. 3600"
            />
            <p className="mt-1 text-sm text-gray-500">Time remaining for offer in seconds</p>
          </div>
        </div>

        {/* Offer Headline */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Offer Headline</label>
          <input
            type="text"
            name="offerHeadline"
            value={formData.offerHeadline}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
            placeholder="Special Limited Time Offer!"
          />
          <p className="mt-1 text-sm text-gray-500">Main attention-grabbing text</p>
        </div>

        {/* Offer Description */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Offer Description</label>
          <textarea
            name="offerParagraph"
            value={formData.offerParagraph}
            onChange={handleChange}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
            placeholder="Describe your offer in detail..."
          ></textarea>
          <p className="mt-1 text-sm text-gray-500">Details about your special offer</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Button Text */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Button Text</label>
            <input
              type="text"
              name="offerButton"
              value={formData.offerButton}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              placeholder="e.g. Claim Offer Now"
            />
            <p className="mt-1 text-sm text-gray-500">Text for the call-to-action button</p>
          </div>

          {/* Redirect Page */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">Redirect URL</label>
            <input
              type="text"
              name="redirectPage"
              value={formData.redirectPage}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
              placeholder="https://example.com/offer"
            />
            <p className="mt-1 text-sm text-gray-500">Where users go after clicking</p>
          </div>
        </div>

        {/* Facebook Pixel */}
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Facebook Pixel ID (Optional)</label>
          <input
            type="text"
            name="pixelId"
            value={formData.pixelId}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
            placeholder="e.g. 123456789012345"
          />
          <p className="mt-1 text-sm text-gray-500">For tracking conversions</p>
        </div>

        {/* Bonus Links Section */}
        <div className="border-t pt-6 mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-800">Bonus Links</h3>
            <button
              type="button"
              onClick={addBonusLink}
              className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"
            >
              + Add Link
            </button>
          </div>

          {formData.bonus_links.map((link, index) => (
            <div key={index} className="bg-gray-50 p-4 rounded-lg mb-4 border">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-medium text-gray-700">Bonus Link #{index + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeBonusLink(index)}
                  className="px-2 py-1 bg-red-600 text-white rounded-md text-xs hover:bg-red-700"
                >
                  Remove
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Type</label>
                  <select
                    value={link.type}
                    onChange={(e) => handleBonusLinkChange(index, 'type', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                  >
                    <option value="gift">Free Gift</option>
                    <option value="download">Download Link</option>
                    <option value="secret_group">Secret Group Link</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={link.title}
                    onChange={(e) => handleBonusLinkChange(index, 'title', e.target.value)}
                    placeholder="e.g. Free Ebook"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Button Text</label>
                  <input
                    type="text"
                    value={link.button_text}
                    onChange={(e) => handleBonusLinkChange(index, 'button_text', e.target.value)}
                    placeholder="e.g. Download Now"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Link URL</label>
                  <input
                    type="text"
                    value={link.link}
                    onChange={(e) => handleBonusLinkChange(index, 'link', e.target.value)}
                    placeholder="https://example.com/download"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Show Time (seconds)</label>
                  <input
                    type="number"
                    value={link.show_time}
                    onChange={(e) => handleBonusLinkChange(index, 'show_time', parseInt(e.target.value) || 0)}
                    placeholder="e.g. 300"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                  />
                  <p className="mt-1 text-xs text-gray-500">When to show this link (in seconds)</p>
                </div>

                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Duration (seconds)</label>
                  <input
                    type="number"
                    value={link.duration}
                    onChange={(e) => handleBonusLinkChange(index, 'duration', parseInt(e.target.value) || 60)}
                    placeholder="e.g. 60"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                  />
                  <p className="mt-1 text-xs text-gray-500">How long to show this link</p>
                </div>
              </div>

              <div className="space-y-1 mb-3">
                <label className="block text-sm font-medium text-gray-700">Description (Optional)</label>
                <textarea
                  value={link.description}
                  onChange={(e) => handleBonusLinkChange(index, 'description', e.target.value)}
                  rows={2}
                  placeholder="Short description of this bonus..."
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                ></textarea>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">Order</label>
                  <input
                    type="number"
                    value={link.order}
                    onChange={(e) => handleBonusLinkChange(index, 'order', parseInt(e.target.value) || 0)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 p-2 border"
                  />
                </div>

                <div className="space-y-1 flex items-center">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={link.is_active}
                      onChange={(e) => handleBonusLinkChange(index, 'is_active', e.target.checked)}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Submit and Cancel Buttons */}
        <div className="flex justify-end space-x-3 pt-6 border-t">
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className={`px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
          >
            {loading ? 'Updating...' : 'Update Live Page'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UpdateLive;